<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-tools me-2"></i>{{ $t('page.equipment', 'Equipment & Asset Register') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="openAdd"><i class="mdi mdi-plus me-1"></i>New Asset</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading"><i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i></button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filters.status = ''; filters.due = 0; loadAll()" :class="{ selected: !filters.status && !filters.due }">
          <div class="hms-stat-num">{{ stats.total || 0 }}</div>
          <div class="hms-stat-lbl">Total Assets</div>
        </div>
        <div class="hms-stat" @click="filters.status = 'in_service'; loadAll()" :class="{ selected: filters.status==='in_service' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.in_service || 0 }}</div>
          <div class="hms-stat-lbl">In Service</div>
        </div>
        <div class="hms-stat" @click="filters.status = 'under_maintenance'; loadAll()" :class="{ selected: filters.status==='under_maintenance' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.under_maintenance || 0 }}</div>
          <div class="hms-stat-lbl">Under Maintenance</div>
        </div>
        <div class="hms-stat" @click="filters.due = 1; filters.status=''; loadAll()" :class="{ selected: filters.due === 1 }">
          <div class="hms-stat-num hms-stat-danger">{{ (stats.calibration_overdue || 0) + (stats.service_overdue || 0) }}</div>
          <div class="hms-stat-lbl">Overdue (Cal+Svc)</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-warning">{{ stats.warranty_expired || 0 }}</div>
          <div class="hms-stat-lbl">Warranty Expired</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">KSh {{ formatNum(stats.total_current_value) }}</div>
          <div class="hms-stat-lbl">Estimated Value</div>
        </div>
      </div>

      <!-- Category breakdown -->
      <div class="hms-card mb-3" v-if="byCategory.length">
        <div class="hms-card-title"><i class="mdi mdi-chart-pie me-1"></i>By Category</div>
        <div class="hms-cat-grid">
          <div v-for="c in byCategory" :key="c.category"
               class="hms-cat" :class="{ active: filters.category === c.category }"
               @click="filters.category = (filters.category === c.category ? '' : c.category); loadAll()">
            <i class="mdi" :class="catIcon(c.category)"></i>
            <div>
              <div class="hms-cat-num">{{ c.cnt }}</div>
              <div class="hms-cat-lbl">{{ formatCategory(c.category) }}</div>
              <div class="hms-muted-sm">KSh {{ formatNum(c.value) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter bar + table -->
      <div class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="filters.search" placeholder="Search tag, name, manufacturer, model, S/N..." @input="debouncedLoad" />
          </div>
          <select class="hms-input hms-filter" v-model="filters.category" @change="loadAll">
            <option value="">All categories</option>
            <option v-for="c in categories" :key="c" :value="c">{{ formatCategory(c) }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.status" @change="loadAll">
            <option value="">All statuses</option>
            <option value="in_service">In Service</option>
            <option value="under_maintenance">Under Maintenance</option>
            <option value="decommissioned">Decommissioned</option>
            <option value="lost">Lost</option>
            <option value="reserved">Reserved</option>
          </select>
          <span class="hms-muted ms-auto">{{ assets.length }} assets</span>
        </div>

        <div v-if="loading && !assets.length" class="hms-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="assets.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Tag</th>
              <th>Asset</th>
              <th>Category</th>
              <th>Location</th>
              <th>Condition</th>
              <th>Status</th>
              <th>Calibration</th>
              <th>Service</th>
              <th>Value</th>
              <th></th>
            </tr></thead>
            <tbody>
              <tr v-for="a in assets" :key="a.asset_id" :class="{ overdue: a.calibration_overdue == 1 || a.service_overdue == 1 }" @click="openDetail(a.asset_id)" class="hms-row-click">
                <td><span class="hms-mono">{{ a.asset_tag }}</span></td>
                <td>
                  <div><strong>{{ a.name }}</strong></div>
                  <div class="hms-muted-sm">{{ [a.manufacturer, a.model].filter(Boolean).join(' · ') || '—' }}{{ a.serial_number ? ' · S/N ' + a.serial_number : '' }}</div>
                </td>
                <td><span class="hms-badge hms-badge-info"><i class="mdi me-1" :class="catIcon(a.category)"></i>{{ formatCategory(a.category) }}</span></td>
                <td>
                  <div>{{ a.ward_name || a.location || '—' }}</div>
                  <div class="hms-muted-sm" v-if="a.assigned_to_name">{{ a.assigned_to_name }}</div>
                </td>
                <td><span class="hms-badge" :class="conditionBadge(a.condition_state)">{{ a.condition_state }}</span></td>
                <td><span class="hms-badge" :class="statusBadge(a.status)">{{ formatStatus(a.status) }}</span></td>
                <td>
                  <span v-if="a.requires_calibration == 1">
                    <span v-if="a.calibration_overdue == 1" class="hms-badge hms-badge-danger">{{ Math.abs(a.days_to_calibration) }}d overdue</span>
                    <span v-else-if="a.next_calibration_date" class="hms-muted-sm">in {{ a.days_to_calibration }}d</span>
                    <span v-else class="hms-muted-sm">—</span>
                  </span>
                  <span v-else class="hms-muted-sm">N/A</span>
                </td>
                <td>
                  <span v-if="a.service_overdue == 1" class="hms-badge hms-badge-danger">{{ Math.abs(a.days_to_service) }}d overdue</span>
                  <span v-else-if="a.next_service_date" class="hms-muted-sm">in {{ a.days_to_service }}d</span>
                  <span v-else class="hms-muted-sm">—</span>
                </td>
                <td><strong>KSh {{ formatNum(a.current_value) }}</strong></td>
                <td><i class="mdi mdi-chevron-right text-muted"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-tools"></i>No assets match.</div>
      </div>

      <!-- ============ DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="detail" @click.self="detail=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-tools me-1"></i>{{ detail.Asset.asset_tag }} · {{ detail.Asset.name }}</span>
            <button class="hms-modal-close" @click="detail=null">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Manufacturer / Model</span><span>{{ [detail.Asset.manufacturer, detail.Asset.model].filter(Boolean).join(' · ') || '—' }}</span></div>
            <div v-if="detail.Asset.serial_number"><span class="hms-kv-key">Serial #</span><span class="hms-mono">{{ detail.Asset.serial_number }}</span></div>
            <div v-if="detail.Asset.supplier"><span class="hms-kv-key">Supplier</span><span>{{ detail.Asset.supplier }}</span></div>
            <div v-if="detail.Asset.purchase_date"><span class="hms-kv-key">Purchased</span><span>{{ formatDate(detail.Asset.purchase_date) }} · KSh {{ formatNum(detail.Asset.purchase_cost) }}</span></div>
            <div v-if="detail.Asset.warranty_expiry"><span class="hms-kv-key">Warranty</span><span :class="warrantyExpired(detail.Asset) ? 'text-danger' : ''">{{ formatDate(detail.Asset.warranty_expiry) }}{{ warrantyExpired(detail.Asset) ? ' (expired)' : '' }}</span></div>
            <div><span class="hms-kv-key">Location</span><span>{{ detail.Asset.ward_name || detail.Asset.location || '—' }}</span></div>
            <div v-if="detail.Asset.assigned_to_name"><span class="hms-kv-key">Assigned to</span><span>{{ detail.Asset.assigned_to_name }}</span></div>
            <div><span class="hms-kv-key">Condition</span><span><span class="hms-badge" :class="conditionBadge(detail.Asset.condition_state)">{{ detail.Asset.condition_state }}</span></span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="statusBadge(detail.Asset.status)">{{ formatStatus(detail.Asset.status) }}</span></span></div>
            <div v-if="detail.Asset.requires_calibration == 1">
              <span class="hms-kv-key">Calibration</span>
              <span>Last {{ formatDate(detail.Asset.last_calibration_date) }} · Next {{ formatDate(detail.Asset.next_calibration_date) }}{{ detail.Asset.calibration_frequency_months ? ' (every ' + detail.Asset.calibration_frequency_months + 'mo)' : '' }}</span>
            </div>
            <div><span class="hms-kv-key">Service</span><span>Last {{ formatDate(detail.Asset.last_service_date) }} · Next {{ formatDate(detail.Asset.next_service_date) }} (every {{ detail.Asset.service_frequency_months }}mo)</span></div>
            <div><span class="hms-kv-key">Current Value</span><span><strong>KSh {{ formatNum(detail.Asset.current_value) }}</strong> <span class="hms-muted-sm">· {{ detail.Asset.depreciation_rate_pct }}%/yr depreciation</span></span></div>
            <div v-if="detail.Asset.notes"><span class="hms-kv-key">Notes</span><span class="hms-pre-text">{{ detail.Asset.notes }}</span></div>
          </div>

          <div class="d-flex gap-2 flex-wrap mb-3">
            <button class="hms-btn hms-btn-primary" @click="openMaintenance"><i class="mdi mdi-wrench me-1"></i>Log Maintenance</button>
            <button class="hms-btn hms-btn-amber" @click="openEdit(detail.Asset)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
            <button class="hms-btn hms-btn-danger ms-auto" @click="confirmDelete(detail.Asset)"><i class="mdi mdi-trash-can-outline me-1"></i>Delete</button>
          </div>

          <div class="hms-section-label">MAINTENANCE HISTORY ({{ detail.Maintenance.length }})</div>
          <div v-if="detail.Maintenance.length" class="hms-mlist">
            <div v-for="m in detail.Maintenance" :key="m.maintenance_id" class="hms-m" :class="'mtype-' + m.type">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="hms-badge" :class="typeBadge(m.type)">{{ m.type }}</span>
                <span class="hms-badge" :class="mStatusBadge(m.status)">{{ m.status }}</span>
                <span v-if="m.cost > 0" class="hms-muted-sm">KSh {{ formatNum(m.cost) }}</span>
                <span class="hms-muted-sm ms-auto">{{ formatDateTime(m.performed_at || m.scheduled_date) }}</span>
              </div>
              <div class="hms-muted-sm mt-1" v-if="m.performed_by_name || m.performed_by_vendor">
                <i class="mdi mdi-account-wrench"></i>
                {{ m.performed_by_name || m.performed_by_vendor }}
              </div>
              <div v-if="m.findings" class="hms-pre-text mt-1">{{ m.findings }}</div>
              <div v-if="m.parts_replaced" class="hms-muted-sm mt-1"><i class="mdi mdi-cog-sync-outline"></i> Parts: {{ m.parts_replaced }}</div>
              <div v-if="m.next_due_date" class="hms-muted-sm mt-1"><i class="mdi mdi-calendar-clock"></i> Next due: {{ formatDate(m.next_due_date) }}</div>
              <div class="d-flex gap-1 mt-2" v-if="m.status === 'scheduled' || m.status === 'in_progress'">
                <button class="hms-btn hms-btn-sm hms-btn-success" @click="markComplete(m)"><i class="mdi mdi-check me-1"></i>Mark Complete</button>
              </div>
            </div>
          </div>
          <div v-else class="hms-muted">No maintenance recorded yet.</div>
        </div>
      </div>

      <!-- ============ ADD / EDIT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showForm" @click.self="showForm=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="form.asset_id ? 'mdi-pencil' : 'mdi-plus-circle'"></i>{{ form.asset_id ? ' Edit Asset' : ' Register New Asset' }}</span>
            <button class="hms-modal-close" @click="showForm=false">&times;</button>
          </div>
          <div v-if="formError" class="hms-alert hms-alert-danger">{{ formError }}</div>

          <div class="hms-section-label">IDENTIFICATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Asset Tag</label>
              <input class="hms-input" v-model="form.asset_tag" placeholder="Auto-generated if blank" />
            </div>
            <div class="col-md-8"><label class="hms-label">Name *</label>
              <input class="hms-input" v-model="form.name" placeholder="e.g. Siemens Acuson Ultrasound" />
            </div>
            <div class="col-md-4"><label class="hms-label">Category *</label>
              <select class="hms-input" v-model="form.category">
                <option v-for="c in categories" :key="c" :value="c">{{ formatCategory(c) }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Subcategory</label>
              <input class="hms-input" v-model="form.subcategory" placeholder="Imaging / Anaesthesia / Theatre..." />
            </div>
            <div class="col-md-4"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="form.status">
                <option value="in_service">In Service</option>
                <option value="under_maintenance">Under Maintenance</option>
                <option value="reserved">Reserved</option>
                <option value="decommissioned">Decommissioned</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Manufacturer</label>
              <input class="hms-input" v-model="form.manufacturer" />
            </div>
            <div class="col-md-4"><label class="hms-label">Model</label>
              <input class="hms-input" v-model="form.model" />
            </div>
            <div class="col-md-4"><label class="hms-label">Serial Number</label>
              <input class="hms-input" v-model="form.serial_number" />
            </div>
          </div>

          <div class="hms-section-label">PROCUREMENT</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Supplier</label>
              <input class="hms-input" v-model="form.supplier" />
            </div>
            <div class="col-md-3"><label class="hms-label">Purchase Date</label>
              <input type="date" class="hms-input" v-model="form.purchase_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Purchase Cost (KSh)</label>
              <input type="number" class="hms-input" v-model.number="form.purchase_cost" step="100" />
            </div>
            <div class="col-md-2"><label class="hms-label">Warranty Until</label>
              <input type="date" class="hms-input" v-model="form.warranty_expiry" />
            </div>
          </div>

          <div class="hms-section-label">LOCATION & ASSIGNMENT</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Ward (optional)</label>
              <select class="hms-input" v-model="form.ward_id">
                <option value="">— None —</option>
                <option v-for="w in wards" :key="w.ward_id" :value="w.ward_id">{{ w.ward_name }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Free-text Location</label>
              <input class="hms-input" v-model="form.location" placeholder="OPD Consult Room 3" />
            </div>
            <div class="col-md-4"><label class="hms-label">Assigned To</label>
              <select class="hms-input" v-model="form.assigned_to">
                <option value="">— Unassigned —</option>
                <option v-for="u in users" :key="u.user_id" :value="u.user_id">{{ u.first_name }} {{ u.last_name }}</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Condition</label>
              <select class="hms-input" v-model="form.condition_state">
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="decommissioned">Decommissioned</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">CALIBRATION & SERVICING</div>
          <div class="row g-3 mb-3">
            <div class="col-md-3 d-flex align-items-end">
              <label class="hms-toggle">
                <input type="checkbox" :checked="form.requires_calibration == 1" @change="form.requires_calibration = $event.target.checked ? 1 : 0" />
                <span>Requires calibration</span>
              </label>
            </div>
            <div class="col-md-3" v-if="form.requires_calibration == 1"><label class="hms-label">Cal Frequency (mo)</label>
              <input type="number" class="hms-input" v-model.number="form.calibration_frequency_months" min="1" />
            </div>
            <div class="col-md-3" v-if="form.requires_calibration == 1"><label class="hms-label">Last Calibration</label>
              <input type="date" class="hms-input" v-model="form.last_calibration_date" />
            </div>
            <div class="col-md-3" v-if="form.requires_calibration == 1"><label class="hms-label">Next Calibration</label>
              <input type="date" class="hms-input" v-model="form.next_calibration_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Svc Frequency (mo)</label>
              <input type="number" class="hms-input" v-model.number="form.service_frequency_months" min="1" />
            </div>
            <div class="col-md-3"><label class="hms-label">Last Service</label>
              <input type="date" class="hms-input" v-model="form.last_service_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Next Service</label>
              <input type="date" class="hms-input" v-model="form.next_service_date" />
            </div>
          </div>

          <div class="hms-section-label">VALUATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Depreciation Rate (% / yr)</label>
              <input type="number" class="hms-input" v-model.number="form.depreciation_rate_pct" step="0.1" min="0" max="100" />
            </div>
            <div class="col-md-4"><label class="hms-label">Current Value (KSh)</label>
              <input type="number" class="hms-input" v-model.number="form.current_value" step="100" placeholder="Auto-calculated if blank" />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="form.notes"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveAsset" :disabled="processing || !form.name">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (form.asset_id ? 'Save Changes' : 'Register Asset') }}
          </button>
        </div>
      </div>

      <!-- ============ MAINTENANCE LOG MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showMaint" @click.self="showMaint=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-wrench me-1"></i>Log Maintenance</span>
            <button class="hms-modal-close" @click="showMaint=false">&times;</button>
          </div>
          <div v-if="maintError" class="hms-alert hms-alert-danger">{{ maintError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Type *</label>
              <select class="hms-input" v-model="maint.type">
                <option value="preventive">Preventive (PM)</option>
                <option value="corrective">Corrective</option>
                <option value="repair">Repair</option>
                <option value="calibration">Calibration</option>
                <option value="inspection">Inspection</option>
                <option value="installation">Installation</option>
                <option value="decommission">Decommission</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="maint.status">
                <option value="completed">Completed (now)</option>
                <option value="scheduled">Scheduled (future)</option>
                <option value="in_progress">In Progress</option>
              </select>
            </div>
            <div class="col-md-6" v-if="maint.status === 'completed'"><label class="hms-label">Performed At</label>
              <input type="datetime-local" class="hms-input" v-model="maint.performed_at" />
            </div>
            <div class="col-md-6" v-else><label class="hms-label">Scheduled Date</label>
              <input type="date" class="hms-input" v-model="maint.scheduled_date" />
            </div>
            <div class="col-md-6"><label class="hms-label">Vendor (external)</label>
              <input class="hms-input" v-model="maint.performed_by_vendor" placeholder="e.g. Phillips Healthcare" />
            </div>
            <div class="col-md-6"><label class="hms-label">Cost (KSh)</label>
              <input type="number" class="hms-input" v-model.number="maint.cost" step="100" />
            </div>
            <div class="col-md-6"><label class="hms-label">Next Due Date</label>
              <input type="date" class="hms-input" v-model="maint.next_due_date" />
            </div>
            <div class="col-12"><label class="hms-label">Findings / Work Done</label>
              <textarea class="hms-input" rows="3" v-model="maint.findings" placeholder="What was inspected / found / done..."></textarea>
            </div>
            <div class="col-12"><label class="hms-label">Parts Replaced</label>
              <input class="hms-input" v-model="maint.parts_replaced" placeholder="e.g. Filter, X-ray tube, capacitor" />
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveMaintenance" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Log
          </button>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] { --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;--hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;--hms-br:#2a2a2a;--hms-input-br:#333;--hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] { --hms-h:#1a1a1a;--hms-s:#555;--hms-m:#777;--hms-bg1:#fff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#fff;--hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted    { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-danger { color: var(--hms-danger); }
.text-muted { color: var(--hms-m); }
.hms-pre-text { white-space: pre-wrap; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 18px; font-weight: 800; color: var(--hms-h); line-height: 1.2; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-danger  { color: var(--hms-danger); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-cat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; }
.hms-cat { display: flex; gap: 10px; align-items: center; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; cursor: pointer; }
.hms-cat:hover, .hms-cat.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-cat > i { font-size: 22px; color: var(--hms-primary); }
.hms-cat-num { color: var(--hms-h); font-weight: 800; font-size: 18px; line-height: 1; }
.hms-cat-lbl { color: var(--hms-s); font-size: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-row-click { cursor: pointer; }
.hms-row-click:hover td { background: var(--hms-primary-lt); }
.hms-table tr.overdue td { background: rgba(220,53,69,.06); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }
.hms-btn-success { background: var(--hms-success); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 560px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; gap: 8px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 180px; font-weight: 600; }

.hms-mlist { display: flex; flex-direction: column; gap: 10px; }
.hms-m { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); border-radius: 8px; padding: 12px 14px; }
.hms-m.mtype-calibration { border-left-color: var(--hms-info); }
.hms-m.mtype-corrective, .hms-m.mtype-repair { border-left-color: var(--hms-danger); }
.hms-m.mtype-decommission { border-left-color: var(--hms-m); }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const CAT_ICONS = {
  medical_equipment: 'mdi-stethoscope',
  it_equipment:      'mdi-laptop',
  furniture:         'mdi-sofa',
  vehicle:           'mdi-ambulance',
  utility:           'mdi-cog',
  laboratory:        'mdi-flask',
  other:             'mdi-shape-outline'
};

export default {
  name: "EquipmentRegister",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      assets: [], stats: {}, byCategory: [], wards: [], users: [],
      categories: ['medical_equipment','it_equipment','furniture','vehicle','utility','laboratory','other'],
      filters: { category: '', status: '', search: '', due: 0 },
      _searchTimer: null,

      detail: null,
      showForm: false, formError: '',
      form: this.empty(),
      showMaint: false, maintError: '',
      maint: { type: 'preventive', status: 'completed', performed_at: '', scheduled_date: '', performed_by_vendor: '', cost: 0, parts_replaced: '', findings: '', next_due_date: '' },

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
    this.loadLookups();
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    showToast(msg, isErr) {
      this.toastMsg = msg; this.toastError = !!isErr;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var s = this; this.toastTimer = setTimeout(() => s.toastMsg = '', 2400);
    },
    formatNum(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(); },
    formatDate(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    formatCategory(c) { return ({ medical_equipment:'Medical Equipment', it_equipment:'IT Equipment', furniture:'Furniture', vehicle:'Vehicle', utility:'Utility', laboratory:'Laboratory', other:'Other' })[c] || c; },
    formatStatus(s) { return ({ in_service:'In Service', under_maintenance:'Maintenance', decommissioned:'Decommissioned', lost:'Lost', disposed:'Disposed', reserved:'Reserved' })[s] || s; },
    catIcon(c) { return CAT_ICONS[c] || 'mdi-shape-outline'; },
    statusBadge(s) { return ({ in_service:'hms-badge-success', under_maintenance:'hms-badge-warning', decommissioned:'hms-badge-neutral', lost:'hms-badge-danger', disposed:'hms-badge-neutral', reserved:'hms-badge-info' })[s] || 'hms-badge-neutral'; },
    conditionBadge(c) { return ({ excellent:'hms-badge-success', good:'hms-badge-success', fair:'hms-badge-warning', poor:'hms-badge-danger', decommissioned:'hms-badge-neutral' })[c] || 'hms-badge-neutral'; },
    typeBadge(t) { return ({ calibration:'hms-badge-info', preventive:'hms-badge-success', corrective:'hms-badge-warning', repair:'hms-badge-danger', inspection:'hms-badge-info', installation:'hms-badge-purple', decommission:'hms-badge-neutral' })[t] || 'hms-badge-neutral'; },
    mStatusBadge(s) { return ({ scheduled:'hms-badge-info', in_progress:'hms-badge-warning', completed:'hms-badge-success', cancelled:'hms-badge-neutral', overdue:'hms-badge-danger' })[s] || 'hms-badge-neutral'; },
    warrantyExpired(a) { if (!a.warranty_expiry) return false; return new Date(a.warranty_expiry) < new Date(); },

    empty() { return { asset_id: null, asset_tag: '', name: '', category: 'medical_equipment', subcategory: '', manufacturer: '', model: '', serial_number: '', supplier: '', purchase_date: '', purchase_cost: 0, warranty_expiry: '', ward_id: '', location: '', assigned_to: '', condition_state: 'good', status: 'in_service', requires_calibration: 0, calibration_frequency_months: null, last_calibration_date: '', next_calibration_date: '', service_frequency_months: 6, last_service_date: '', next_service_date: '', depreciation_rate_pct: 10, current_value: null, notes: '' }; },

    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var s = this; this._searchTimer = setTimeout(() => s.loadAll(), 350);
    },
    async loadAll() {
      this.loading = true;
      try {
        var p = Object.assign({ function: "getEquipmentAssets" }, this.filters);
        var r = await executeJson(p, hms);
        if (r.data && r.data.success) {
          this.assets     = (r.data.data && r.data.data.Assets)     || [];
          this.stats      = (r.data.data && r.data.data.Stats)      || {};
          this.byCategory = (r.data.data && r.data.data.ByCategory) || [];
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadLookups() {
      try {
        var res = await Promise.all([
          executeJson({ function: "getWardsAndBeds" }, hms),
          executeJson({ function: "getUsers" }, hms)
        ]);
        if (res[0].data && res[0].data.success) {
          // getWardsAndBeds returns ward+bed rows; dedupe wards
          var raw = (res[0].data.data && res[0].data.data.Wards) || (res[0].data.data && res[0].data.data.Records) || [];
          var seen = {}; this.wards = [];
          raw.forEach(w => { if (w.ward_id && !seen[w.ward_id]) { seen[w.ward_id] = 1; this.wards.push({ ward_id: w.ward_id, ward_name: w.ward_name }); } });
        }
        if (res[1].data && res[1].data.success) this.users = (res[1].data.data && res[1].data.data.Users) || [];
      } catch(e) {}
    },

    async openDetail(id) {
      try {
        var r = await executeJson({ function: "getEquipmentDetail", asset_id: id }, hms);
        if (r.data && r.data.success) this.detail = r.data.data;
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    openAdd() { this.form = this.empty(); this.formError = ''; this.showForm = true; },
    openEdit(a) {
      this.form = Object.assign(this.empty(), a, {
        purchase_date:         (a.purchase_date || '').slice(0, 10),
        warranty_expiry:       (a.warranty_expiry || '').slice(0, 10),
        last_calibration_date: (a.last_calibration_date || '').slice(0, 10),
        next_calibration_date: (a.next_calibration_date || '').slice(0, 10),
        last_service_date:     (a.last_service_date || '').slice(0, 10),
        next_service_date:     (a.next_service_date || '').slice(0, 10),
        ward_id:               a.ward_id || '',
        assigned_to:           a.assigned_to || '',
        current_value:         a.current_value !== null && a.current_value !== '' ? parseFloat(a.current_value) : null
      });
      this.formError = ''; this.detail = null; this.showForm = true;
    },
    async saveAsset() {
      this.formError = ''; this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveEquipmentAsset" }, this.form), hms);
        if (r.data && r.data.success) { this.showToast(this.form.asset_id ? 'Updated' : 'Asset registered'); this.showForm = false; await this.loadAll(); }
        else this.formError = r.data.message || 'Failed';
      } catch(e) { this.formError = 'Connection error'; }
      this.processing = false;
    },
    async confirmDelete(a) {
      if (!confirm('Delete asset "' + a.name + '"?\n\nThis is a soft delete; the record stays in the database flagged disposed.')) return;
      try {
        var r = await executeJson({ function: 'deleteEquipmentAsset', asset_id: a.asset_id }, hms);
        if (r.data && r.data.success) { this.showToast('Deleted'); this.detail = null; await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    openMaintenance() {
      this.maint = { type: 'preventive', status: 'completed', performed_at: this.nowLocal(), scheduled_date: '', performed_by_vendor: '', cost: 0, parts_replaced: '', findings: '', next_due_date: '' };
      this.maintError = '';
      this.showMaint = true;
    },
    nowLocal() {
      var d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') + 'T' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0');
    },
    async saveMaintenance() {
      this.maintError = ''; this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "logEquipmentMaintenance", asset_id: this.detail.Asset.asset_id }, this.maint), hms);
        if (r.data && r.data.success) {
          this.showToast('Maintenance logged');
          this.showMaint = false;
          await this.openDetail(this.detail.Asset.asset_id);
          await this.loadAll();
        } else this.maintError = r.data.message || 'Failed';
      } catch(e) { this.maintError = 'Connection error'; }
      this.processing = false;
    },
    async markComplete(m) {
      try {
        var r = await executeJson({ function: 'completeMaintenance', maintenance_id: m.maintenance_id }, hms);
        if (r.data && r.data.success) { this.showToast('Completed'); await this.openDetail(this.detail.Asset.asset_id); await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    }
  }
};
</script>
